import React, { useCallback, useMemo, useState } from "react";
import BrandLoader from '../../shared/brand/BrandLoader';
import { getIntlLocale } from '../../../utils/functions';
import {
  PiFileLight,
  PiTrash,
  PiArrowSquareOutLight,
  PiCloudArrowDownLight,
  PiDownloadLight,
  PiTrashLight,
} from "react-icons/pi";
import SSECreationProgress from "../../shared/SSECreationProgress";
import { AttachmentDropzone } from "../../shared/attachments";
import Table from "../../shared/TailwindComponents/Table";
import { useOrgMembers } from "../../../hooks/useOrgMembers";
import FilterBarWithTabs from "../../shared/TailwindComponents/FilterBarWithTabs";
import { useAppDispatch } from "../../../store";
import { showModal, hideModal } from "../../../store/actions/modalActions";

interface FileItem {
  id?: string;
  name: string;
  url?: string;
  size?: number;
  contentType?: string;
  lastModified?: string;
  /** What the files.json record actually carries (see list_files). */
  uploaded_at?: string;
  /** The uploader's user uuid. */
  uploaded_by?: string;
}

interface DataSourceFilesProps {
  t: (key: string) => string;
  filesInIndex: FileItem[];
  searchTerm: string;
  setSearchTerm: (val: string) => void;
  deletingFiles: string[];
  handleOpenFile: (fileId: string | undefined, name: string) => void;
  handleDownloadFile: (fileId: string | undefined, name: string) => void;
  handleCopyLink: (file: FileItem) => void;
  handleDeleteFile: (fileId: string, fileName: string) => void;
  isFetchingFiles: boolean;
  filesToAdd: File[];
  setFilesToAdd: (files: File[]) => void;
  handleAddData: (files: File[]) => void;
  isFilesLoading: boolean;
  isConnecting: boolean;
  steps: any[];
  connected: boolean;
  complete: boolean;
  ok: boolean | null;
  summary?: string;
  startedAt?: number;
  isReindexing?: boolean;
}

const formatSize = (bytes?: number) => {
  if (!bytes && bytes !== 0) return "-";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let i = -1;
  do {
    bytes = bytes / 1024;
    i++;
  } while (bytes >= 1024 && i < units.length - 1);
  return `${bytes.toFixed(1)} ${units[i]}`;
};

const ext = (name: string) => (name.split(".").pop() || "").toLowerCase();

const getFileTypeBadge = (name: string) => {
  const e = ext(name);
  const typeMap: Record<string, { label: string; color: string }> = {
    pdf: { label: "PDF", color: "red" },
    doc: { label: "DOC", color: "blue" },
    docx: { label: "DOCX", color: "blue" },
    txt: { label: "TXT", color: "gray" },
    md: { label: "MD", color: "gray" },
    xls: { label: "XLS", color: "green" },
    xlsx: { label: "XLSX", color: "green" },
    csv: { label: "CSV", color: "green" },
    jpg: { label: "JPG", color: "purple" },
    jpeg: { label: "JPEG", color: "purple" },
    png: { label: "PNG", color: "purple" },
  };
  return typeMap[e] || { label: e.toUpperCase() || "FILE", color: "gray" };
};

const prettyDate = (d?: string | number | Date) => {
  if (!d) return "-";
  const dt = typeof d === "string" || typeof d === "number" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return "-";
  return new Intl.DateTimeFormat(getIntlLocale(), { dateStyle: 'medium' }).format(dt);
};

const DataSourceFiles: React.FC<DataSourceFilesProps> = ({
  t,
  filesInIndex,
  searchTerm,
  setSearchTerm,
  deletingFiles,
  handleOpenFile,
  handleDownloadFile,
  handleCopyLink,
  handleDeleteFile,
  filesToAdd,
  setFilesToAdd,
  handleAddData,
  isFilesLoading,
  isConnecting,
  steps,
  connected,
  complete,
  ok,
  summary,
  startedAt,
  isFetchingFiles,
  isReindexing,
}) => {
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const dispatch = useAppDispatch();
  /* `uploaded_by` is a user uuid; the org roster turns it into a name. A
     member the roster cannot resolve (left the org, no people:read) shows a
     muted dash rather than a raw id. */
  const { members } = useOrgMembers();
  const memberNames = useMemo(() => {
    const map = new Map<string, string>();
    members.forEach((m) => map.set(String(m.id), m.name));
    return map;
  }, [members]);
  const uploaderName = useCallback(
    (file: FileItem) => (file.uploaded_by ? memberNames.get(String(file.uploaded_by)) : undefined),
    [memberNames]
  );
  /* The record stores `uploaded_at`; `lastModified` is the older shape. */
  const fileDate = (file: FileItem) => file.lastModified || file.uploaded_at;

  // Table columns
  const columns = ["Name", "Type", "Size", "Uploaded by", "Last Modified", "Actions"];
  const sortableColumns = ["Name", "Size", "Uploaded by", "Last Modified"];

  // Map column headers to sort keys
  const columnSortMap: Record<string, string> = {
    Name: "name",
    Size: "size",
    "Uploaded by": "uploader",
    "Last Modified": "date",
  };

  const sortedFilteredFiles = useMemo(() => {
    const filtered = filesInIndex.filter((file) =>
      file.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const sorted = [...filtered].sort((a, b) => {
      const dir = sortOrder === "asc" ? 1 : -1;
      if (sortBy === "name" || sortBy === "Name") {
        return a.name.localeCompare(b.name) * dir;
      } else if (sortBy === "size" || sortBy === "Size") {
        return ((a.size || 0) - (b.size || 0)) * dir;
      } else if (sortBy === "uploader") {
        return (uploaderName(a) || "").localeCompare(uploaderName(b) || "") * dir;
      } else {
        const ad = new Date(fileDate(a) || 0).getTime();
        const bd = new Date(fileDate(b) || 0).getTime();
        return (ad - bd) * dir;
      }
    });
    return sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesInIndex, searchTerm, sortBy, sortOrder, uploaderName]);

  // Transform file data for table
  const tableData = useMemo(() => {
    return sortedFilteredFiles.map((file) => {
      const fileType = getFileTypeBadge(file.name);
      return {
        id: file.id || file.name,
        Name: file.name,
        Type: (
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${
              fileType.color === "red"
                ? "bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300"
                : fileType.color === "blue"
                ? "bg-info-50 text-info-700 dark:bg-info-900/30 dark:text-info-300"
                : fileType.color === "green"
                ? "bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-300"
                : fileType.color === "purple"
                ? "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                : "bg-surface-sunken text-fg-body"
            }`}
          >
            {fileType.label}
          </span>
        ),
        Size: formatSize(file.size),
        "Uploaded by": uploaderName(file) || <span className="text-fg-muted">—</span>,
        "Last Modified": prettyDate(fileDate(file)),
        _file: file, // Keep original file data for actions
      };
    });
  }, [sortedFilteredFiles, uploaderName]);

  const handleSort = (column: string, order: "asc" | "desc") => {
    setSortBy(columnSortMap[column] || column);
    setSortOrder(order);
  };

  const handleRowClick = (row: any) => {
    const file = row._file as FileItem;
    if (file) {
      handleOpenFile(file.id, file.name);
    }
  };

  const tableActions = [
    {
      label: t("Open"),
      icon: <PiArrowSquareOutLight className="h-4 w-4" />,
      onClick: (row: any) => {
        const file = row._file as FileItem;
        handleOpenFile(file.id, file.name);
      },
    },
    {
      label: t("Download"),
      icon: <PiDownloadLight className="h-4 w-4" />,
      onClick: (row: any) => {
        const file = row._file as FileItem;
        handleDownloadFile(file.id, file.name);
      },
    },
    {
      label: t("Delete"),
      icon: <PiTrash className="h-4 w-4" />,
      onClick: (row: any) => {
        const file = row._file as FileItem;
        handleDeleteFile(file.id || file.name, file.name);
      },
      isDestructive: true,
      disabled: (row: any) => deletingFiles.includes(row.id),
    },
  ];

  const handleAddDataModal = useCallback(async () => {
    try {
      dispatch(
        showModal({
          showModal: true,
          type: "uploadAttachment",
          onConfirm: async (files: File[]) => {
            setFilesToAdd(files);
            await handleAddData(files);
          },
          message: t("Upload data"),
          icon: <PiCloudArrowDownLight className="w-6 h-6 text-gray-600" />,
          subMsg: t("Upload more data to your data source."),
          files: filesToAdd,
        })
      );
    } catch (error) {
      console.error(t("Error archiving task. Please try again later."), error);
    }
  }, [dispatch, filesToAdd, handleAddData, setFilesToAdd, t]);

  const handleSelectRow = (id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === tableData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(tableData.map((r) => r.id));
    }
  };

  const handleBulkDelete = useCallback(() => {
    if (selectedRows.length === 0) return;
    dispatch(
      showModal({
        type: "confirmation",
        message: t(`Are you sure you want to delete ${selectedRows.length} file(s)?`),
        subMsg: t("This action cannot be undone."),
        icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
        isDest: true,
        showModal: true,
        onConfirm: async () => {
          for (const id of selectedRows) {
            const file = sortedFilteredFiles.find(
              (f) => (f.id || f.name) === id
            );
            if (file) {
              handleDeleteFile(file.id || file.name, file.name);
            }
          }
          setSelectedRows([]);
          dispatch(hideModal());
        },
        onCancel: () => {},
        inputData: "Yes, Delete",
      })
    );
  }, [selectedRows, dispatch, sortedFilteredFiles, handleDeleteFile, t]);

  const filterBarActions = useMemo(() => {
    const acts: { label: string; onClick: () => void; icon: React.ReactElement }[] = [
      {
        label: t("Add data"),
        onClick: handleAddDataModal,
        icon: <PiCloudArrowDownLight className="h-4 w-4" />,
      },
    ];
    if (selectedRows.length > 0) {
      acts.push({
        label: `${t("Delete")} (${selectedRows.length})`,
        onClick: handleBulkDelete,
        icon: <PiTrash className="h-4 w-4" />,
      });
    }
    return acts;
  }, [selectedRows, handleAddDataModal, handleBulkDelete, t]);

  if (isFilesLoading) {
    return (
      /* The upload's SSE progress, in place of the list it is adding to —
         never a fixed overlay. The upload itself is driven by the PAGE, so
         this view can come and go without touching it. */
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main mt-4">
        <SSECreationProgress
          title={t('Uploading files to source')}
          isConnecting={isConnecting}
          steps={steps}
          connected={connected}
          complete={complete}
          ok={ok}
          summary={summary}
          startedAt={startedAt}
          onCancel={() => {}}
        />
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col transition-all duration-300">
      <div data-tour-id="ds-files-filter-bar">
      <FilterBarWithTabs
        itemCount={tableData.length}
        totalItemCount={filesInIndex.length}
        itemCountLabel="files"
        onSearchChange={setSearchTerm}
        actions={filterBarActions}
        noParentPadding={true}
        noParentHeight={true}
      />
      </div>

      {isReindexing && (
        <div className="mx-0 mt-2 mb-1 flex items-center gap-2 rounded-md bg-info-50 dark:bg-info-900/30 px-4 py-2.5 text-sm text-info-700 dark:text-info-300 border border-info-200 dark:border-info-800">
          <BrandLoader size="xs" className="!h-4 !w-4" />
          <span>{t('Reprocessing data source… This may take a moment.')}</span>
        </div>
      )}

      {/* An EMPTY source asks to be filled, so the shared dropzone stands in
          for the table's "no rows" plate - same `handleAddData` the modal
          calls, so the SSE upload the page drives is untouched. Once there is
          a file to list, the table takes the space back and "Add Data" is the
          filter bar's button. */}
      {!isFetchingFiles && filesInIndex.length === 0 ? (
        <div data-tour-id="ds-files-table" className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main pb-4 pt-4">
          <AttachmentDropzone
            onUpload={handleAddData}
            accept={["pdf", "doc", "docx", "txt", "md", "xls", "xlsx", "csv"]}
            label={t("Drop files to build this data source")}
            hint={t("Upload files to get started.")}
          />
        </div>
      ) : (
      /* The table claims the flex space it is given. It used to sit under an
          invisible 120px-tall absolutely-positioned div that existed only to
          anchor a tour step - the anchor is on the real container now. */
      <div data-tour-id="ds-files-table" className="flex-1 min-h-0 pb-4 flex flex-col">
        <Table
          columns={columns}
          data={isFetchingFiles ? [] : tableData}
          onRowClick={handleRowClick}
          actions={tableActions}
          sortableColumns={sortableColumns}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          /* No viewport-relative height inside the surface: the canvas has
             already computed how much room this tab gets. See the height
             contract in `shared/surface/README.md`. */
          max="max-h-full"
          min="min-h-0"
          resizableColumns={true}
          storageKeyPrefix="datasource-files"
          enableSelection={true}
          selectedIds={selectedRows}
          onSelect={handleSelectRow}
          onSelectAll={handleSelectAll}
          isLoadingMore={isFetchingFiles}
          total_items={filesInIndex.length}
          total_showing_items={tableData.length}
          setIsShowing={true}
          showingLabel={t('files')}
          defaultColumnWidths={{
            Name: 300,
            Type: 100,
            Size: 100,
            "Uploaded by": 160,
            "Last Modified": 150,
          }}
          /* An empty SOURCE is the dropzone above; the only way to reach an
             empty table now is a search that matches nothing, so this says
             that rather than "upload files to get started". */
          emptyState={{
            icon: <PiFileLight className="h-12 w-12" />,
            headline: t("No files match your search."),
            subtext: t("Try a different search term."),
          }}
        />
      </div>
      )}
    </div>
  );
};

export default DataSourceFiles;
